import {
  headsAndTailsScope,
  PRODUCT_MAPPING,
  ProductType,
} from "../../openapi-typescript/common/product_types";
import { UserScopes } from "../../openapi-typescript/common/user_scopes";
import { buildQuerySelect, keysToSnake } from "../global";
import { FeatureFlag, useFeatureFlags } from "./useFeatureFlags";
import { useUpdatables } from "./useUpdatable";

export type ProductClassType =
  | "carbon"
  | "environmental"
  | "electricity"
  | "electricity-shapes"
  | "rehub"
  | "hourly-power-shapes"
  | "au-hourly-shapes"
  | "power";
export type DailyCurveProductType =
  | "accu"
  | "nzu"
  | "vcu"
  | "gs"
  | "cer"
  | "smc"
  | "esc"
  | "lgc"
  | "prc"
  | "stc"
  | "veec"
  | "irec"
  | "rego"
  | "ASX Electricity"
  | "Electricity Shapes"
  | "Heads & Tails"
  | "Hourly power shapes";
export const carbonProductTypes: DailyCurveProductType[] = [
  "accu",
  "smc",
  "nzu",
  "vcu",
  "gs",
  "cer",
];
export const environmentalProductTypes: DailyCurveProductType[] = [
  "lgc",
  "stc",
  "prc",
  "veec",
  "esc",
  "irec",
];
export const electricityProductTypes: DailyCurveProductType[] = [
  "ASX Electricity",
  "Electricity Shapes",
];
const dailyCurveCarbon = "benchmarks-carbon";
const dailyCurveEnviron = "benchmarks-environmental";
const dailyCurvePower = "benchmarks-power";

export const electricityHourlyShapesProductTypes = [
  {
    code: "mn",
    name: "Morning",
  },
  {
    code: "mp",
    name: "Morning Peak",
  },
  {
    code: "dy",
    name: "Daytime",
  },
  {
    code: "ep",
    name: "Evening Peak",
  },
  {
    code: "ev",
    name: "Evening",
  },
  {
    code: "nt",
    name: "Nighttime",
  },
];

export const electricityHeadsAndTailsProductTypes = Object.values(
  headsAndTailsScope
).map((code) => ({ code, name: PRODUCT_MAPPING[code] }));

export const sortByCarbonDailyCurveOrder = (arr: string[]) => {
  const orderMap = new Map(
    carbonProductTypes.map((item, index) => [item.toLowerCase(), index])
  );

  return [...arr].sort(
    (a, b) =>
      (orderMap.get(a.toLowerCase()) ?? Infinity) -
      (orderMap.get(b.toLowerCase()) ?? Infinity)
  );
};

export const sortByEnvironmentalDailyCurveOrder = (arr: string[]) => {
  const orderMap = new Map(
    environmentalProductTypes.map((item, index) => [item.toLowerCase(), index])
  );

  return [...arr].sort(
    (a, b) =>
      (orderMap.get(a.toLowerCase()) ?? Infinity) -
      (orderMap.get(b.toLowerCase()) ?? Infinity)
  );
};

export const getProductTypePermission = (
  features: FeatureFlag[],
  selectedProductType?: string
): boolean => {
  return selectedProductType
    ? features.includes(
        `core.product.${selectedProductType}.read` as UserScopes
      )
    : true;
};

export const useDailyCurveFeaturePermissions = (
  selectedProductType?: DailyCurveProductType
): boolean => {
  const { features } = useFeatureFlags();
  if (!features?.includes("core.daily-curves.read")) return false;

  if (selectedProductType) {
    const isCarbon = carbonProductTypes.includes(selectedProductType);
    const isEnviron = environmentalProductTypes.includes(selectedProductType);
    const hasPowerAccess = features?.includes(dailyCurvePower);

    return isCarbon
      ? features?.includes(dailyCurveCarbon)
      : isEnviron
      ? features?.includes(dailyCurveEnviron)
      : hasPowerAccess;
  }

  return (
    features?.includes(dailyCurveCarbon) ||
    features?.includes(dailyCurveEnviron) ||
    features?.includes(dailyCurvePower)
  );
};

export const useProductTypeScopes = () => {
  const { features } = useFeatureFlags();
  const regExScope = new RegExp(/core.product./);

  return features?.filter((f) => f.match(regExScope)) ?? [];
};

export const useAllProductTypesByScopes = () => {
  const availableScopes = useProductTypeScopes();
  return [
    ...new Set(
      availableScopes.flatMap((s) => {
        const parts = s.split(".");
        const product = parts[2] as DailyCurveProductType;
        return product;
      })
    ),
  ];
};

export const useDailyCurveCarbonProductTypes = () => {
  const availableScopes = useProductTypeScopes();
  const carbonProducts = [
    ...new Set(
      availableScopes.flatMap((s) => {
        const parts = s.split(".");
        const product = parts[2] as DailyCurveProductType;
        return carbonProductTypes.includes(product) ? product : [];
      })
    ),
  ];
  return sortByCarbonDailyCurveOrder(carbonProducts);
};

export const useDailyCurveEnvironmentProductTypes = () => {
  const availableScopes = useProductTypeScopes();
  const enviProducts = [
    ...new Set(
      availableScopes.flatMap((s) => {
        const parts = s.split(".");
        const product = parts[2] as DailyCurveProductType;
        return environmentalProductTypes.includes(product) ? product : [];
      })
    ),
  ];

  return sortByEnvironmentalDailyCurveOrder(enviProducts);
};

export const useDailyCurveProductTypes = (): {
  productClass: ProductClassType;
  productTypes: string[];
}[] => {
  const { features } = useFeatureFlags();
  const dailyCurvePermission = useDailyCurveFeaturePermissions();
  const carbons = useDailyCurveCarbonProductTypes();
  const environs = useDailyCurveEnvironmentProductTypes();

  if (!dailyCurvePermission) return [];

  return [
    ...(features?.includes(dailyCurveCarbon)
      ? [
          {
            productClass: "carbon" as ProductClassType,
            productTypes: carbons,
          },
        ]
      : []),
    ...(features?.includes(dailyCurveEnviron)
      ? [
          {
            productClass: "environmental" as ProductClassType,
            productTypes: environs,
          },
        ]
      : []),
  ];
};

export type UseAllProductTypesParams = {
  productClasses?: ProductType["productClass"][];
  code?: ProductType["code"][];
  order?: string;
  dailyCurvesSortOrder?: string;
  dailyCurvesCustomerVisible?: string;
};

export const useAllProductTypes = (args?: UseAllProductTypesParams) => {
  const {
    productClasses,
    code,
    order,
    dailyCurvesSortOrder,
    dailyCurvesCustomerVisible,
  } = args ?? {};

  return useUpdatables<ProductType>({
    get: [
      "getAllProductTypes",
      {
        select: buildQuerySelect({
          code: "code",
          name: "name",
          requiredScope: "required_scope",
        }),
        ...keysToSnake({
          ...(productClasses?.length
            ? { productClass: `in.(${productClasses.join(",")})` }
            : {}),
          ...(code?.length && { code: `in.(${code.join(",")})` }),
          ...(order && { order: order }),
          ...(dailyCurvesSortOrder && {
            dailyCurvesSortOrder: dailyCurvesSortOrder,
          }),
          ...(dailyCurvesCustomerVisible && {
            dailyCurvesCustomerVisible: dailyCurvesCustomerVisible,
          }),
        }),
      },
    ],
    transformToRichTypes: (record) => record,
  });
};
